import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import PublicNav from "@/components/PublicNav";
import PublicFooter from "@/components/PublicFooter";
import usePageMeta from "@/hooks/usePageMeta";
import useBranding from "@/hooks/useBranding";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, MapPin, GraduationCap, ShieldCheck, Sparkles, ArrowRight } from "lucide-react";

const HERO_IMG = "https://images.pexels.com/photos/2982449/pexels-photo-2982449.jpeg?auto=compress&cs=tinysrgb&w=1600";
const CLASSROOM_IMG = "https://images.pexels.com/photos/37811241/pexels-photo-37811241.jpeg?auto=compress&cs=tinysrgb&w=940";
const MENTOR_IMG = "https://images.pexels.com/photos/8926900/pexels-photo-8926900.jpeg?auto=compress&cs=tinysrgb&w=940";

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [city, setCity] = useState("");
  const [level, setLevel] = useState("");
  const nav = useNavigate();
  const brand = useBranding();
  usePageMeta(
    brand?.site_title ? `${brand.site_title} — Find the right school for your child` : "School Dial — Find the right school for your child",
    brand?.site_description || "Discover, compare and enquire with top schools across India — free & instant.",
  );

  useEffect(() => {
    api.get("/schools", { params: { featured_only: true, limit: 6 } }).then(r => setFeatured(r.data));
  }, []);

  const goSearch = (e) => {
    e?.preventDefault();
    const p = new URLSearchParams();
    if (city) p.set("city", city);
    if (level) p.set("school_type", level);
    nav(`/schools?${p.toString()}`);
  };

  return (
    <div className="min-h-screen bg-white">
      <PublicNav />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={brand?.hero_image_url || HERO_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-slate-900/60" />
        </div>
        <div className="relative mx-auto max-w-7xl px-6 py-24 lg:py-32">
          <div className="max-w-3xl">
            <Badge className="mb-6 bg-amber-400/95 text-slate-900 hover:bg-amber-400" data-testid="hero-badge">
              {brand?.hero_badge || "India's most trusted school directory"}
            </Badge>
            <h1 className="text-white font-black tracking-tighter text-5xl md:text-6xl lg:text-7xl leading-[0.95]"
                style={{ textShadow: "0 2px 20px rgba(0,0,0,0.35)" }}>
              {brand?.hero_title_line1 || "Find the right school."}<br />
              <span className="text-amber-400">{brand?.hero_title_line2 || "Shape a better future."}</span>
            </h1>
            <p className="mt-6 text-lg text-slate-200 max-w-2xl" style={{ textShadow: "0 1px 10px rgba(0,0,0,0.4)" }}>
              {brand?.hero_subtitle || "Compare 500+ verified schools across India — pre-primary to senior secondary. Filter by fees, facilities, board and admission status."}
            </p>

            <form onSubmit={goSearch} className="mt-10 bg-white rounded-lg p-2 flex flex-col md:flex-row gap-2 shadow-2xl max-w-3xl" data-testid="hero-search-form">
              <div className="flex-1 flex items-center gap-2 px-3">
                <MapPin className="w-4 h-4 text-slate-400" />
                <Input
                  data-testid="hero-city-input"
                  placeholder="City (e.g. Delhi, Mumbai)"
                  value={city} onChange={(e) => setCity(e.target.value)}
                  className="border-0 focus-visible:ring-0 shadow-none"
                />
              </div>
              <div className="md:w-56">
                <Select value={level} onValueChange={setLevel}>
                  <SelectTrigger data-testid="hero-level-select" className="border-0 shadow-none">
                    <SelectValue placeholder="School Level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Pre">Pre-Primary</SelectItem>
                    <SelectItem value="Primary">Primary</SelectItem>
                    <SelectItem value="Middle">Middle</SelectItem>
                    <SelectItem value="Secondary">Secondary</SelectItem>
                    <SelectItem value="Sr. Secondary">Sr. Secondary</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" data-testid="hero-search-btn" className="bg-slate-900 hover:bg-slate-800 h-11">
                <Search className="w-4 h-4 mr-2" /> Search
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-6 py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { n: "500+", l: "Verified schools" },
            { n: "40+", l: "Cities covered" },
            { n: "25k", l: "Parents helped" },
            { n: "4.8★", l: "Average rating" },
          ].map((s, i) => (
            <div key={i} className="text-center md:text-left">
              <div className="text-3xl md:text-4xl font-black tracking-tight text-slate-900">{s.n}</div>
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mt-1">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Featured schools */}
      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-amber-600 mb-3">
              <Sparkles className="w-3 h-3 inline mr-2" /> Featured
            </div>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900">Top-rated schools</h2>
          </div>
          <Link to="/schools" className="text-sm font-semibold text-slate-900 hover:text-slate-600 hidden md:flex items-center gap-1" data-testid="view-all-schools">
            View all <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {featured.map((s) => (
            <Link to={`/schools/${s.slug || s.id}`} key={s.id} className="group" data-testid={`featured-card-${s.id}`}>
              <div className="border border-slate-200 rounded-lg overflow-hidden bg-white transition-all duration-200 group-hover:-translate-y-1 group-hover:shadow-lg">
                <div className="aspect-[16/10] bg-slate-100 overflow-hidden">
                  <img src={s.feature_image_url || CLASSROOM_IMG} alt={s.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-2">
                    {s.verified && <Badge variant="outline" className="border-emerald-300 text-emerald-700"><ShieldCheck className="w-3 h-3 mr-1" /> Verified by School</Badge>}
                    {s.admission_open && <Badge className="bg-emerald-600 hover:bg-emerald-600">Admission Open</Badge>}
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 group-hover:text-blue-700">{s.name}</h3>
                  <div className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                    <MapPin className="w-3 h-3" /> {s.city}, {s.state}
                  </div>
                  <div className="mt-4 flex items-center justify-between text-xs text-slate-600">
                    <span className="font-semibold">{s.board}</span>
                    <span>₹{(s.fees_min || 0).toLocaleString()} - ₹{(s.fees_max || 0).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* CTA bento */}
      <section className="mx-auto max-w-7xl px-6 pb-20">
        <div className="grid md:grid-cols-3 gap-6">
          <div className="md:col-span-2 relative rounded-lg overflow-hidden bg-slate-900 min-h-[280px] flex items-end p-8">
            <img src={MENTOR_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
            <div className="relative">
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-amber-400 mb-2">For School Owners</div>
              <h3 className="text-2xl md:text-3xl font-bold text-white">List your school on School Dial</h3>
              <p className="text-slate-300 mt-2 max-w-lg">Get discovered by thousands of parents actively searching for the right school.</p>
              <Link to="/list-your-school">
                <Button className="mt-4 bg-amber-400 text-slate-900 hover:bg-amber-500" data-testid="cta-list-school">
                  Submit your school
                </Button>
              </Link>
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 p-8 bg-white">
            <GraduationCap className="w-8 h-8 text-blue-700" />
            <h3 className="text-xl font-bold text-slate-900 mt-4">Already a member?</h3>
            <p className="text-sm text-slate-500 mt-2">School admins, teachers and parents can log in to manage admissions, fees, attendance and more.</p>
            <Link to="/login">
              <Button variant="outline" className="mt-4 w-full" data-testid="cta-login">Login to your account</Button>
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
